import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarSumarizacaoNotasFiscaisApuracao,
  buscarLancamentosNotasFiscaisApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro, { FILTER_FIELD_SX } from "../components/SelectFiltro";
import { formatarCnpj, fmtMesAno } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

function exportarCsv(linhas, nomeArquivo) {
  const cabecalho = [
    "data_emissao",
    "numero",
    "serie",
    "modelo",
    "cnpj",
    "razao_social",
    "cfop",
    "valor_nota",
  ].join(";");
  const corpo = linhas.map((l) =>
    [l.dataEmissao, l.numero, l.serie ?? "", l.modelo ?? "", l.cnpj ?? "", l.razaoSocial ?? "", l.cfop ?? "", l.valorNota]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(";")
  );
  const blob = new Blob([[cabecalho, ...corpo].join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LancamentosNotasFiscais() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // TXM-597 — competência e tipo NÃO são filtros interativos nesta tela: o card pede só
  // "busca por número/CNPJ/razão social, filtro por CFOP" (protótipo
  // apuracao_icms_notas_lancamentos.html não tem seletor de competência/tipo). Ambos são
  // fixados pela linha clicada na aba (chegam via ?competencia=/&tipo= na URL, mostrados só
  // como texto no subtítulo); pra ver outra competência/tipo, o usuário volta e clica em
  // "Detalhes" na linha desejada.
  const competencia = searchParams.get("competencia") || "";
  const tipo = searchParams.get("tipo") || "Entrada";

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [busca, setBusca] = useState("");
  const [cfopFiltro, setCfopFiltro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [linhasPagina, setLinhasPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregarApuracao() {
      const res = await buscarSumarizacaoNotasFiscaisApuracao(id);
      if (!ativo || !res.sucesso) return;
      setApuracao(res.apuracao || null);
    }
    carregarApuracao();
    return () => {
      ativo = false;
    };
  }, [id]);

  const carregarLancamentos = useCallback(async () => {
    if (!competencia) {
      setLoading(false);
      setErro("Competência não informada.");
      return;
    }
    setLoading(true);
    setErro("");
    const res = await buscarLancamentosNotasFiscaisApuracao(id, competencia, tipo);
    setLoading(false);
    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      return;
    }
    setLinhas(res.linhas);
  }, [id, competencia, tipo]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  const cfopOpcoes = useMemo(() => {
    const cfops = Array.from(new Set(linhas.map((l) => l.cfop).filter(Boolean))).sort();
    return [{ value: "", label: "Todos" }, ...cfops.map((c) => ({ value: c, label: c }))];
  }, [linhas]);

  function limparFiltros() {
    setBusca("");
    setCfopFiltro("");
  }

  const linhasFiltradas = useMemo(() => {
    let base = linhas;
    const q = busca.trim().toLowerCase();
    if (q) {
      base = base.filter(
        (l) =>
          String(l.numero || "").includes(q) ||
          String(l.cnpj || "").includes(q.replace(/\D/g, "")) ||
          String(l.razaoSocial || "").toLowerCase().includes(q)
      );
    }
    if (cfopFiltro) base = base.filter((l) => l.cfop === cfopFiltro);
    return base;
  }, [linhas, busca, cfopFiltro]);

  if (loading && linhas.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  return (
    // navigate(-1) em vez de reconstruir a URL: `id` aqui é o id da apuração Notas Fiscais
    // (filha), não o id do pai ICMS que /detalhe/icms espera — diferente do CAT-42, não
    // existe uma tela standalone "/detalhe/notas-fiscais" para onde voltar com o id da filha.
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        { label: `Apuração ICMS — ${apuracao?.empresa || "—"}`, onClick: () => navigate(-1) },
        { label: "Lançamentos de Notas Fiscais" },
      ]}
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
        conteudoComScrollProprio
        Icone={ReceiptLongIcon}
        titulo="Lançamentos de Notas Fiscais"
        subtitulo={
          <>
            {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
            <br />
            Competência: {competencia ? fmtMesAno(competencia) : "—"} · Tipo: {tipo}
          </>
        }
        acaoHeader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {linhasFiltradas.length} notas no filtro
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={linhasFiltradas.length === 0}
                onClick={() =>
                  exportarCsv(
                    linhasFiltradas,
                    `lancamentos_notas_fiscais_${formatarCnpj(apuracao?.cnpj)}_${competencia}_${tipo}.csv`
                  )
                }
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
              >
                Exportar CSV
              </Button>
            </Box>
          }
          filtros={
            // TXM-597 — só os filtros que o card pede pra esta tela (busca por
            // número/CNPJ/razão social + CFOP), mesma ordem do protótipo
            // apuracao_icms_notas_lancamentos.html.
            <>
              <TextField
                size="small"
                label="Número, CNPJ ou Nome"
                placeholder="Pesquisar número, CNPJ"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                sx={{ ...FILTER_FIELD_SX, width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#3c4043" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <SelectFiltro label="CFOP" value={cfopFiltro} onChange={setCfopFiltro} options={cfopOpcoes} />
              <Button
                variant="outlined"
                size="small"
                onClick={limparFiltros}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              >
                Limpar filtros
              </Button>
            </>
          }
          paginacao={
            !loading &&
            linhasFiltradas.length > 0 && (
              <Pagination
                paginationKey="lancamentos-notas-fiscais"
                data={linhasFiltradas}
                filteredData={linhasPagina}
                setFilteredData={setLinhasPagina}
              />
            )
          }
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <GridLoader color="#1565c0" size={10} />
            </Box>
          ) : linhasFiltradas.length === 0 && !erro ? (
            <p className={styles.emptyMsg}>Nenhuma nota fiscal encontrada para este filtro.</p>
          ) : (
              <div style={{ margin: "8px 0 0", overflow: "auto" }}>
                <table className={tableStyles.accordion_table}>
                  <thead>
                    <tr>
                      <th scope="col">Data emissão</th>
                      <th scope="col">Número</th>
                      <th scope="col">Série</th>
                      <th scope="col">Modelo</th>
                      <th scope="col">CNPJ</th>
                      <th scope="col">Razão social</th>
                      <th scope="col">CFOP</th>
                      <th scope="col" style={{ textAlign: "right" }}>Valor da nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasPagina.map((l, i) => (
                      <tr key={`${l.numero}-${i}`}>
                        <td>{l.dataEmissao || "—"}</td>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.numero || "—"}</td>
                        <td>{l.serie || "—"}</td>
                        <td>{l.modelo || "—"}</td>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{formatarCnpj(l.cnpj)}</td>
                        <td>{l.razaoSocial || "—"}</td>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.cfop || "—"}</td>
                        <td style={{ textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{formatarMoedaBRL(l.valorNota)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}
      </ApuracaoCardShell>
    </BreadcrumbApuracao>
  );
}
